import {
  MenuItem,
  Menu,
  Button,
  Typography,
  withStyles,
  ListItemText,
} from '@material-ui/core';
import React from 'react';
import clsx from 'clsx';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { makeStyles } from '@material-ui/styles';
import palette from 'theme/palette';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import ApplicationState from 'store/interfaces';
import { connect } from 'react-redux';
import { inAdminView, inSuperAdminView, inOperatorView } from 'utils/role';
import styles from './styles';

export interface Option {
  id?: string;
  label: string;
  onClick: () => void;
}

interface Props {
  options: Option[];
  quantity?: number;
  currentValue: Option;
  switchFirstOption?: string;
  idHtml?: string;
  role?: RolesEnum;
  companySelected: CompanyData | null;
  overrideClasses?: any;
}
const FilterSelector: React.FC<Props> = ({
  options,
  quantity,
  currentValue,
  switchFirstOption,
  idHtml,
  role,
  companySelected,
  overrideClasses,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedElement, setSelectedElement] = React.useState<Option>(
    currentValue
  );

  React.useEffect(() => {
    setSelectedElement(currentValue);
  }, [currentValue]);

  const useStyles = makeStyles(styles(overrideClasses));

  const classes = useStyles({});

  const open = Boolean(anchorEl);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const StyledMenu: any = withStyles({
    paper: {
      border: `0.1rem solid ${palette.superLightGray}`,
    },
  })(props => (
    <Menu
      elevation={0}
      getContentAnchorEl={null}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      open={open}
      disableScrollLock={true}
      defaultValue={selectedElement.label}
      {...props}
    />
  ));

  const StyledMenuItem = withStyles(() => ({
    root: {
      '&:focus': {
        backgroundColor: palette.borderGray,
        '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
          color: palette.darkGray,
        },
        '.MuiListItem-root.Mui-selected, & .MuiListItem-root.Mui-selected:hover': {
          backgroundColor: palette.borderGray,
        },
      },
    },
  }))(MenuItem);

  return (
    <div>
      <Button
        id={idHtml || 'filter-selector-button'}
        className={classes.filterButton}
        aria-controls="customized-menu"
        aria-haspopup="true"
        color={undefined}
        onClick={handleClick}
        endIcon={
          open ? (
            <ExpandLessIcon
              className={clsx(classes.endIcon, {
                [classes.endIconGreen]: inAdminView(role, companySelected),
                [classes.endIconGray]: inSuperAdminView(role, companySelected),
                [classes.endIconBlue]: inOperatorView(role, companySelected),
                [classes.endIconYellow]: companySelected,
              })}
            />
          ) : (
            <ExpandMoreIcon
              className={clsx(classes.endIcon, {
                [classes.endIconGreen]: inAdminView(role, companySelected),
                [classes.endIconGray]: inSuperAdminView(role, companySelected),
                [classes.endIconBlue]: inOperatorView(role, companySelected),
                [classes.endIconYellow]: companySelected,
              })}
            />
          )
        }
      >
        <Typography
          variant="body1"
          className={clsx(classes.text, {
            [classes.textGreen]: inAdminView(role, companySelected),
            [classes.textGray]: inSuperAdminView(role, companySelected),
            [classes.textBlue]: inOperatorView(role, companySelected),
            [classes.textYellow]: companySelected,
          })}
        >
          {quantity
            ? `${selectedElement.label} (${quantity})`
            : selectedElement.label}
        </Typography>
      </Button>
      <StyledMenu
        id="filter-selector-menu"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
      >
        {options.map((option, index) => (
          <StyledMenuItem
            id={option.id}
            key={option.label}
            onClick={event => {
              event.preventDefault();
              setSelectedElement(option);
              option.onClick();
              handleClose();
            }}
            selected={option.label === selectedElement.label}
            className={classes.styledMenu}
          >
            <ListItemText
              className={classes.listItem}
              primary={
                index === 0 ? switchFirstOption || option.label : option.label
              }
            />
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </div>
  );
};

const mapStateToProps = ({
  authenticate: { data, companySelected },
}: ApplicationState) => ({
  companySelected,
  role: data?.role,
});

export default connect(mapStateToProps, {})(FilterSelector);
